import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import { Shield } from "lucide-react";
import axios from "axios";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("username", email);
      formData.append("password", password);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/token`,
        formData
      );

      const { access_token, roles } = response.data;

      // Only allow admins
      if (!roles || !roles.includes("admin")) {
        setError("Access denied. Admin credentials required.");
        return;
      }

      localStorage.setItem("token", access_token);
      localStorage.setItem("user_email", email);
      localStorage.setItem("user_roles", JSON.stringify(roles));

      navigate("/portal/admin/dashboard");
    } catch (err) {
      setError("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 font-sans">
      <Header />

      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 bg-zinc-900 rounded-lg flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-4xl font-heading font-bold uppercase mb-4 text-black" data-testid="admin-login-title">
              Admin Access
            </h1>
            <p className="text-gray-500">
              Restricted area for system administrators.
            </p>
          </div>

          <Card className="w-full shadow-lg border-t-4 border-zinc-900">
            <CardHeader>
              <CardTitle className="text-2xl font-bold">Admin Sign In</CardTitle>
              <CardDescription>Enter admin credentials to continue</CardDescription>
            </CardHeader>
            <form onSubmit={handleLogin}>
              <CardContent className="space-y-4">
                {error && (
                  <div className="p-3 text-sm rounded-md bg-red-50 text-red-600" data-testid="admin-login-error">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="admin-email">Email</Label>
                  <Input
                    id="admin-email"
                    type="email"
                    placeholder="admin@froglake.ca"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="admin-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-password">Password</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="admin-password-input"
                  />
                </div>
              </CardContent>
              <CardFooter>
                <Button
                  type="submit"
                  className="w-full bg-zinc-900 text-white hover:bg-zinc-800 font-bold uppercase tracking-widest"
                  disabled={loading}
                  data-testid="admin-login-submit"
                >
                  {loading ? "Signing in..." : "Admin Sign In"}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
